import i18n from "i18next";
import { removePrefix, sanitize, slugify } from "../../common";
import { isWebapiCalendarEvent, isWebapiCalendarEventArray } from "../../guards";
import { formatImage } from "../../image-asset";
import { getParselyDate } from "../../locale";
import { DATETIME_DISPLAY_FORMATS, zonedTimeFormat } from "../../time";
import { AdTags, AnalyticsTags, ContentBlock, TagContext, Tagger, Tags } from "../type";
import { generateMetaTag, processMetaImage, setGeneralMetaTags } from "../util";

export const calendarTags: Tagger = (state: Tags, context: TagContext) => {
    const { site, block, request } = context;

    const { path } = request;
    const [, currentTag] = path.split("calendar/");
    let currentCategory = currentTag ? currentTag.split("/")[1] : "";
    const stationName: string = site.sections.general?.name || "";
    const weatherMarket: string = site.sections.general?.weather_market || "";
    let parselyTitle = "";
    let title = "";
    let description = "";
    let keywords: string[] = [];
    const topics: string[] = [];

    if (block?.value) {
        currentCategory = typeof block.value.category === "string" ? block.value.category : "";
    }

    if (block?.resolved && isWebapiCalendarEventArray(block.resolved)) {
        const calendarItems = block.resolved;

        calendarItems.forEach(calendar => {
            if (calendar.tags) {
                keywords = calendar.tags.map(removePrefix(/^\w+\//));
            }
        });
    }

    if (currentCategory) {
        topics.push(slugify(removePrefix(/^\w+\//)(currentCategory)));
    }

    if (currentCategory && currentTag) {
        const categoryName = removePrefix(/^events\//)(currentTag);
        const events = categoryName.toLowerCase() !== "concerts" ? "events " : "";

        parselyTitle = `${categoryName} | Calendar`;
        title = `${parselyTitle} | ${stationName}`;
        description = i18n.t("calendar_category_description", {
            currentCategory,
            events,
            weatherMarket,
            stationName,
        });

        keywords = [
            categoryName.toLowerCase(),
            i18n.t("calendar").toLowerCase(),
            i18n.t("events").toLowerCase(),
            weatherMarket.toLowerCase(),
        ];
    } else {
        parselyTitle = i18n.t("calendar_parsely_title", { weatherMarket });
        title = `${parselyTitle} | ${stationName}`;
        description = i18n.t("calendar_description", { weatherMarket, stationName });

        keywords = [
            i18n.t("calendar").toLowerCase(),
            i18n.t("events").toLowerCase(),
            weatherMarket.toLowerCase(),
            i18n.t("community").toLowerCase(),
            i18n.t("concerts").toLowerCase(),
            i18n.t("shows").toLowerCase(),
            i18n.t("weekend"),
            i18n.t("things"),
        ];
    }

    let { metadata } = state;
    metadata = setGeneralMetaTags(metadata, {
        titles: {
            default: title,
            parsely: parselyTitle,
        },
        description,
        keywords,
        types: {
            parsely: "sectionpage",
        },
    });

    metadata.set(...generateMetaTag("name", "parsely-section", "calendar"));

    return {
        ...state,
        ads: {
            ...state.ads,
            keywords: keywords.map(item => slugify(item)),
            topics,
        } as AdTags,
        analytics: {
            ...state.analytics,
            view: {
                ...state.analytics?.view,
                tags: keywords.map(item => slugify(item)),
                topics,
                filter: {
                    name: "calendar_category",
                    type: currentCategory,
                },
            },
        } as AnalyticsTags,
        metadata,
    };
};

export const eventTags: Tagger = (state: Tags, context: TagContext) => {
    const { site, block, env } = context;
    if (block?.resolved && isWebapiCalendarEvent(block.resolved)) {
        const event = block.resolved;
        state.surrogateKeys?.add(`event-id/${slugify(event.eventId)}`);
        state.surrogateKeys?.add(`content/${event.content.slug}`);
        state.surrogateKeys?.add(`content/${decodeURIComponent(event.content.id).replace("/", "")}`);

        let title = "";
        let parselySection = "calendar";
        if (event.content.record?.eyebrow?.topic) {
            parselySection = event.content.record.eyebrow.topic;
        }

        let keywords = event.tags.map(removePrefix(/^\w+\//));
        if (!keywords || keywords.length === 0) {
            keywords = sanitize(event.content.title).split(" ");
        }

        const image = formatImage(event.content.img, env);

        const topics = [];
        topics.push(...event.tags.map((tag: string) => slugify(removePrefix(/^\w+\//)(tag))));

        const stationName = site.sections.general?.name || "";
        const venue = event.venueName;
        const address = event.venueAddress;
        const seoTitle = event.content.title || block.value.title || "";

        const eventDate = zonedTimeFormat({
            date: event.startDateTime,
            timezone: site.index?.timeZone ? site.index.timeZone : env.DEFAULT_TIMEZONE,
            outputFormat: DATETIME_DISPLAY_FORMATS.SHORT_MONTH_DAY_WITH_SUFFIX_FULL_YEAR,
        });

        if (seoTitle) {
            title = `${seoTitle} | ${eventDate} | ${venue}`;
            title = `${title} | ${stationName}`;
            state.surrogateKeys?.add(`event/${slugify(seoTitle)}`);
        }

        let socialTitle = "";
        if (eventDate && venue) {
            socialTitle = event.content.title || seoTitle;
            socialTitle = `${socialTitle} | ${eventDate} | ${venue}`;
        }

        if (venue) {
            keywords.push(...sanitize(venue.trim().toLowerCase()).split(" "));
        }

        if (address) {
            keywords.push(...sanitize(address.trim().toLowerCase().replace(/,/g, "")).split(" "));
        }

        let description = event.content.summary;
        if (!description) {
            const station = `${site.sections.general?.name} - ${site.sections.general?.positioner}`;
            description = i18n.t("event_description", { station, stationName });
        }

        let { metadata } = state;

        metadata = setGeneralMetaTags(metadata, {
            titles: {
                default: title,
                social: socialTitle,
            },
            description,
            keywords,
            image: { url: processMetaImage(image, context) },
            types: {
                parsely: "post",
                openGraph: "article",
            },
            twitter: {
                card: "summary_large_image",
            },
        });

        metadata.set(...generateMetaTag("name", "parsely-section", parselySection));

        let galleryExists = false;
        if (event.content.record && event.content.record.payload && event.content.record.payload.blocks) {
            const galleries = event.content.record.payload.blocks.filter(
                (item: ContentBlock) => item.type === "gallery",
            );
            galleryExists = !!galleries.length;
            metadata.set(...generateMetaTag("name", "canonical", event.content.record.payload.canonical_url));
        }

        const categories: string[] = [];
        [...event.tags, ...event.origins].forEach(item => categories.push(removePrefix(/^categories\//)(item)));

        if (categories.length) {
            metadata.set(...generateMetaTag("name", "categories", categories.join(", ")));
        }

        if (event.content.publish_date) {
            metadata.set(...generateMetaTag("name", "parsely-pub-date", getParselyDate(event.content.publish_date)));
        }

        if (event.content.author) {
            metadata.set(...generateMetaTag("name", "parsely-author", event.content.author));
        }

        return {
            ...state,
            ads: {
                ...state.ads,
                keywords: keywords.map(item => slugify(item)),
            } as AdTags,
            analytics: {
                ...state.analytics,
                view: {
                    ...state.analytics?.view,
                    tags: keywords.map(item => slugify(item)),
                    asset: {
                        name: event.content.title.trim(),
                    },
                    photoGalleryExists: galleryExists.toString(),
                    photoGalleryPageView: galleryExists.toString(),
                },
            } as AnalyticsTags,
            metadata,
        };
    }

    return state;
};
